import {
  MemoryOutboxStore,
  OfflineStoreOutboxAdapter,
  resolveConflictPolicy,
  SyncQueue,
  type ConflictPolicy,
  type ConflictResolution,
  type FetchImpl,
  type OfflineCollectionLike,
  type OutboxEntry,
  type OutboxStore,
  type SyncConflict,
  type SyncQueueConfig,
} from "@norkron/web-sync-queue";
import {
  createStore,
  defineSchema,
  table,
  type TableDefinition,
} from "@norkron/web-offline-store";
import { MUTATION_POLICY, type OutboxTableName } from "./mutations";
import { createTempIdSerializer, TempIdRegistry } from "./tempIds";

/**
 * The elaborate SyncQueue singleton (design §4.0/§4.5). Composes the ecosystem
 * outbox — `@norkron/web-sync-queue` — over a durable `@norkron/web-offline-store`
 * collection, so the queue (and thus the optimistic overlay) survives a reload.
 * NOTHING here reimplements an outbox, backoff, ordering, or connectivity — those
 * are all the library. elaborate owns only the glue: the endpoint, the CSRF +
 * `credentials:'include'` fetch wrapper, the temp-id serializer, and the
 * per-table conflict-resolver seam.
 */

/** The server batch terminal (design §6C) + the durable outbox table name. */
export const SYNC_ENDPOINT = "/api/sync/batch";
export const OUTBOX_TABLE = "sync_outbox";
/** The IndexedDB database backing the durable outbox (via web-offline-store). */
export const OUTBOX_DB = "elaborate-sync";

/**
 * The connectivity heartbeat target (design §4.0). The library's `OnlineDetector`
 * HEAD-probes this on start + every interval to confirm the server is reachable.
 * It MUST be a real same-origin route that answers a HEAD — `/api/health` is the
 * liveness token (200, no DB round-trip). It is deliberately NOT `SYNC_ENDPOINT`:
 * `/api/sync/batch` is POST-only, so a HEAD on it is 404, which (before this fix)
 * pinned the indicator "Offline" forever under the app's `connect-src 'self'` CSP.
 */
export const HEARTBEAT_ENDPOINT = "/api/health";

// ── Confirmation bus: how the host observes per-item server confirmations ────
// `web-sync-queue` marks a confirmed entry `succeeded` but does not surface the
// server row to the host, so the ONE place elaborate sees `{server_row}` is the
// `fetchImpl` it injects. The wrapper tees the batch response into this bus; the
// temp-id registry + any screen listener reconcile off it (design §5.3/§8).

export interface Confirmation {
  clientEventUuid: string;
  tempId?: string;
  table: string;
  operation: string;
  serverRow: unknown;
  serverVersion: number | null;
  status: string;
}

export type ConfirmationListener = (c: Confirmation) => void;

export class ConfirmationBus {
  private readonly listeners = new Set<ConfirmationListener>();
  subscribe(l: ConfirmationListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }
  emit(c: Confirmation): void {
    for (const l of this.listeners) l(c);
  }
}

/** Read a non-HttpOnly cookie value (mirrors `api/realAdapter.ts`'s CSRF read). */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = name + "=";
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return undefined;
}

interface WireItem {
  client_event_uuid: string;
  table: string;
  operation: string;
  payload: { __tempId?: unknown } & Record<string, unknown>;
}
interface WireResult {
  client_event_uuid: string;
  status: string;
  server_row?: unknown;
  server_version?: number;
}

/**
 * The injected fetch: adds elaborate's CSRF double-submit header +
 * `credentials:'include'` (the lib's default `fetch` sets neither), and tees the
 * batch response into `bus`/`registry` so a create's real id becomes known.
 */
export function createFetchImpl(
  bus: ConfirmationBus,
  registry: TempIdRegistry,
  baseFetch: typeof fetch = fetch,
): FetchImpl {
  return async (input: string, init: RequestInit): Promise<Response> => {
    const headers = new Headers(init.headers as HeadersInit | undefined);
    if ((init.method ?? "GET").toUpperCase() !== "GET") {
      const csrf = readCookie("el_csrf");
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }
    const res = await baseFetch(input, {
      ...init,
      headers,
      credentials: "include",
    });

    // Tee ONLY the batch POST (not the HEAD heartbeat) for confirmations.
    const isBatchPost =
      (init.method ?? "GET").toUpperCase() === "POST" &&
      typeof input === "string" &&
      input.endsWith(SYNC_ENDPOINT);
    if (isBatchPost && res.ok) {
      teeConfirmations(init, res, bus, registry).catch(() => {
        /* confirmation-tee is best-effort; never breaks the drain */
      });
    }
    return res;
  };
}

async function teeConfirmations(
  init: RequestInit,
  res: Response,
  bus: ConfirmationBus,
  registry: TempIdRegistry,
): Promise<void> {
  const uuidToTemp = new Map<string, string>();
  const uuidToItem = new Map<string, WireItem>();
  try {
    const reqBody = JSON.parse(String(init.body ?? "{}")) as { items?: WireItem[] };
    for (const it of reqBody.items ?? []) {
      uuidToItem.set(it.client_event_uuid, it);
      const t = it.payload?.__tempId;
      if (typeof t === "string") uuidToTemp.set(it.client_event_uuid, t);
    }
  } catch {
    return;
  }
  const body = (await res.clone().json()) as { results?: WireResult[] };
  for (const r of body.results ?? []) {
    const item = uuidToItem.get(r.client_event_uuid);
    const tempId = uuidToTemp.get(r.client_event_uuid);
    const serverId = extractId(r.server_row);
    if (tempId && serverId !== null) registry.record(tempId, serverId);
    bus.emit({
      clientEventUuid: r.client_event_uuid,
      tempId,
      table: item?.table ?? "",
      operation: item?.operation ?? "",
      serverRow: r.server_row ?? null,
      serverVersion: r.server_version ?? null,
      status: r.status,
    });
  }
}

function extractId(row: unknown): number | null {
  if (row && typeof row === "object" && "id" in row) {
    const v = (row as { id: unknown }).id;
    if (typeof v === "number") return v;
  }
  return null;
}

// ── Conflict-resolver seam (design §4.4): per-table policy, pluggable ────────
// The library accepts a single `ConflictPolicy` function; this default branches
// on `conflict.entry.table` and delegates to the named built-in from the
// per-table `MUTATION_POLICY` (server-authoritative for anything gated). A host
// can pass its own `conflictPolicy` to `buildSyncQueue` to override the seam.

export function perTableConflictPolicy(): ConflictPolicy {
  return (conflict: SyncConflict): ConflictResolution => {
    const name = MUTATION_POLICY[conflict.entry.table as OutboxTableName];
    const policy = resolveConflictPolicy(name ?? "server-authoritative");
    return policy(conflict);
  };
}

// ── Durable collection backing the outbox adapter ────────────────────────────
// `OfflineStoreOutboxAdapter` needs a duck-typed collection (put/query/patch/
// all/delete) with an auto-assigned `id`. This adapts `@norkron/web-offline-store`
// (durable IndexedDB) when it is available, and falls back to an in-memory map
// for SSR / test environments with no IndexedDB — the SAME contract either way,
// so the adapter + engine are exercised identically in both.

type Row = Record<string, unknown>;

/**
 * The minimal slice of `@norkron/web-offline-store`'s `Store` the outbox needs.
 * Structural — declared here so the outbox glue is decoupled from the sibling's
 * exact strict generics (the adapter contract is intentionally tiny, per the
 * lib's own `OfflineStoreOutboxAdapter` docstring).
 */
interface RawStore {
  getAll(table: string): Promise<Array<Record<string, unknown>>>;
  get(table: string, key: number): Promise<Record<string, unknown> | undefined>;
  put(table: string, value: Record<string, unknown>): Promise<unknown>;
  delete(table: string, key: number): Promise<void>;
}

// The outbox row IS the lib's `OutboxEntry`. The table definition is cast to the
// invariant `TableDefinition` at this boundary (the DSL's `TableBuilder<T>` is
// generic-invariant under the app's strict tsconfig, and the store is accessed
// via the structural `RawStore` below, so the schema's inferred row type is
// unused at the call sites).
/** Build a single-table outbox schema for `tableName` (the store key MUST equal
 *  the table name every op below reads/writes). Parametrised so a SECOND durable
 *  queue (e.g. the reactions outbox) can live in its own IndexedDB db + table
 *  without colliding with the batch outbox. */
function buildOutboxSchema(tableName: string) {
  const t = table<OutboxEntry>("id").build() as unknown as TableDefinition;
  return defineSchema({ [tableName]: t }, 1);
}

class OfflineStoreCollection implements OfflineCollectionLike {
  private store: RawStore | null = null;
  private mem: Map<number, Row> | null = null;
  private seq = 0;
  private ready: Promise<void> | null = null;

  constructor(
    private readonly dbName: string = OUTBOX_DB,
    private readonly tableName: string = OUTBOX_TABLE,
  ) {}

  private async init(): Promise<void> {
    if (this.ready) return this.ready;
    this.ready = (async () => {
      const hasIdb =
        typeof globalThis !== "undefined" &&
        typeof (globalThis as { indexedDB?: unknown }).indexedDB !== "undefined";
      if (hasIdb) {
        try {
          this.store = (await createStore({
            dbName: this.dbName,
            schema: buildOutboxSchema(this.tableName),
          })) as unknown as RawStore;
          const rows = await this.store.getAll(this.tableName);
          this.seq = rows.reduce((m, r) => Math.max(m, Number(r.id) || 0), 0);
          return;
        } catch {
          /* fall through to the in-memory backing */
        }
      }
      this.mem = new Map();
    })();
    return this.ready;
  }

  async put(row: Row): Promise<{ id: number }> {
    await this.init();
    const id = typeof row.id === "number" ? row.id : ++this.seq;
    const stored: Row = { ...row, id };
    if (this.store) await this.store.put(this.tableName, stored);
    else this.mem!.set(id, stored);
    return { id };
  }

  async query(pred: (row: Row) => boolean): Promise<Row[]> {
    await this.init();
    const rows = await this.all();
    return rows.filter(pred);
  }

  async patch(id: number, partial: Row): Promise<void> {
    await this.init();
    if (this.store) {
      const cur = await this.store.get(this.tableName, id);
      if (!cur) return;
      await this.store.put(this.tableName, { ...cur, ...partial, id });
    } else {
      const cur = this.mem!.get(id);
      if (!cur) return;
      this.mem!.set(id, { ...cur, ...partial, id });
    }
  }

  async all(): Promise<Row[]> {
    await this.init();
    if (this.store) return await this.store.getAll(this.tableName);
    return [...this.mem!.values()].map((r) => ({ ...r }));
  }

  async delete(id: number): Promise<void> {
    await this.init();
    if (this.store) await this.store.delete(this.tableName, id);
    else this.mem!.delete(id);
  }
}

/** Build the durable outbox store (own db + table), or the in-memory fallback
 *  for tests/SSR. Parametrised so a second durable queue can be isolated. */
export function buildOutboxStore(
  dbName: string = OUTBOX_DB,
  tableName: string = OUTBOX_TABLE,
): OutboxStore {
  try {
    return new OfflineStoreOutboxAdapter(
      new OfflineStoreCollection(dbName, tableName),
    );
  } catch {
    return new MemoryOutboxStore();
  }
}

/** Options for {@link buildSyncQueue} (all optional — sensible elaborate defaults). */
export interface BuildSyncQueueOpts {
  store?: OutboxStore;
  fetchImpl?: FetchImpl;
  conflictPolicy?: ConflictPolicy;
  bus?: ConfirmationBus;
  registry?: TempIdRegistry;
  /** Override the config (endpoint/batchSize/heartbeat) for tests. */
  config?: Partial<SyncQueueConfig>;
}

export interface SyncQueueBundle {
  queue: SyncQueue;
  bus: ConfirmationBus;
  registry: TempIdRegistry;
}

/**
 * Construct a SyncQueue wired with elaborate's endpoint, durable store, CSRF +
 * credentials fetch wrapper, temp-id serializer, and per-table conflict seam.
 */
export function buildSyncQueue(opts: BuildSyncQueueOpts = {}): SyncQueueBundle {
  const bus = opts.bus ?? new ConfirmationBus();
  const registry = opts.registry ?? new TempIdRegistry();
  const store = opts.store ?? buildOutboxStore();
  const fetchImpl = opts.fetchImpl ?? createFetchImpl(bus, registry);
  const config: SyncQueueConfig = {
    endpoint: SYNC_ENDPOINT,
    heartbeatUrl: HEARTBEAT_ENDPOINT,
    outboxTable: OUTBOX_TABLE,
    store,
    fetchImpl,
    serializer: createTempIdSerializer(registry),
    conflictPolicy: opts.conflictPolicy ?? perTableConflictPolicy(),
    ...opts.config,
  };
  return { queue: new SyncQueue(config), bus, registry };
}

// ── The process-wide singleton (lazily built; started by SyncProvider) ───────
let g_bundle: SyncQueueBundle | null = null;

/** The lazily-constructed SyncQueue bundle for the app. */
export function getSyncQueue(): SyncQueueBundle {
  if (!g_bundle) g_bundle = buildSyncQueue();
  return g_bundle;
}

/** Start draining + connectivity detection (idempotent). */
export function startSyncQueue(): Promise<void> {
  return getSyncQueue().queue.start();
}

/** Stop draining + timers (idempotent). */
export function stopSyncQueue(): void {
  g_bundle?.queue.stop();
}

/** Test-only: drop the singleton so the next `getSyncQueue` rebuilds it. */
export function __resetSyncQueueForTests(): void {
  g_bundle?.queue.stop();
  g_bundle = null;
}
